import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { orderService, costService, shippingService } from "../services/dataService";
import { ModelCost, Order, OrderShipmentSummary, OrderStatus, OrderWorkshopCost, ShipmentStatus } from "../types";
import { formatCurrency, formatNumber, getImageUrl } from "../utils/formatters";
import { usePermissions } from "../context/PermissionContext";
import { useAuth } from "../context/AuthContext";
import PageLoader from "../components/PageLoader";
import AlertModal from "../components/AlertModal";
import OrderEditModal from "../components/OrderEditModal";
import "./Receivables.css";
import "./OrderDetail.css";

/* ---------- tipler ---------- */
interface OrderLog {
  logId: string; changedAt: string; changeType: string; fieldName?: string;
  oldValue?: string; newValue?: string; changedByUserName?: string; changeDescription?: string; changeToken?: string;
}
interface LogGroup { token: string; changedAt: string; user: string; changes: OrderLog[] }

/* ---------- sabitler / yardımcılar ---------- */
const STATUS_LABEL: Record<number, string> = {
  [OrderStatus.Atanmadi]: "Atanmadı", [OrderStatus.Islemde]: "İşlemde", [OrderStatus.IptalEdildi]: "İptal Edildi", [OrderStatus.Tamamlandi]: "Tamamlandı",
};
const UNIT_LABEL: Record<number, string> = { 1: "Adet", 2: "Metre", 3: "Takım", 7: "Pano" };
const FIELD_LABEL: Record<string, string> = {
  Status: "Durum", StatusChanged: "Durum", Workshop: "Atölye", WorkshopChanged: "Atölye", Operator: "Sorumlu", OperatorChanged: "Sorumlu",
  AssignedUser: "Sorumlu", Price: "Fiyat", PriceCurrency: "Para birimi", Quantity: "Miktar", Priority: "Öncelik", Deadline: "Termin",
  Note: "Not", Model: "Model", Firm: "Firma", Modelist: "Modelist", Images: "Görseller", Unit: "Birim", Technics: "Teknikler",
  Created: "Oluşturuldu", Updated: "Güncellendi", AcceptanceDate: "Kabul tarihi", CompletionDate: "Tamamlanma", IsSample: "Numune",
};
const fieldLabel = (l: OrderLog) => FIELD_LABEL[l.fieldName || ""] || FIELD_LABEL[l.changeType] || l.fieldName || l.changeType;

const fmtDate = (iso?: string | null) => { if (!iso) return "—"; const d = new Date(iso); return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric" }); };
const fmtDateTime = (iso?: string | null) => { if (!iso) return "—"; const d = new Date(iso); return isNaN(d.getTime()) ? "—" : d.toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); };
const fmtQty = (n?: number) => (n ?? 0).toLocaleString("tr-TR", { maximumFractionDigits: 3 });

const spanText = (fromIso?: string, toIso?: string | null) => {
  if (!fromIso) return null;
  const a = new Date(fromIso).getTime(), b = (toIso ? new Date(toIso) : new Date()).getTime();
  if (isNaN(a) || isNaN(b)) return null;
  const h = Math.max(0, Math.floor((b - a) / 3600000)); const d = Math.floor(h / 24);
  return d > 0 ? `${d} gün${h % 24 ? ` ${h % 24} sa` : ""}` : `${Math.max(1, h)} saat`;
};
const deadlineInfo = (deadline?: string | null, status?: OrderStatus) => {
  if (!deadline) return { tone: "", text: "" };
  if (status === OrderStatus.Tamamlandi) return { tone: "ok", text: "tamamlandı" };
  if (status === OrderStatus.IptalEdildi) return { tone: "", text: "" };
  const t = new Date(); t.setHours(0, 0, 0, 0); const d = new Date(deadline); d.setHours(0, 0, 0, 0);
  const days = Math.round((d.getTime() - t.getTime()) / 86400000);
  if (days < 0) return { tone: "overdue", text: `${-days} gün gecikti` };
  if (days === 0) return { tone: "warn", text: "bugün" };
  return { tone: days <= 3 ? "warn" : "", text: `${days} gün kaldı` };
};
/** Para birimine göre topla — farklı birimleri tek sayıya indirgemek yanıltır */
const sumByCurrency = (rows: { totalCost?: number | null; currency?: string | null }[]) =>
  rows.reduce<Record<string, number>>((m, r) => { const c = (r.currency || "TRY").toUpperCase(); m[c] = (m[c] || 0) + (r.totalCost || 0); return m; }, {});
const moneyList = (m: Record<string, number>) => Object.entries(m).map(([c, v]) => formatCurrency(v, c));

const I = {
  back: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" width="16" height="16"><path d="M15 6l-6 6 6 6" /></svg>,
  edit: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" /></svg>,
  qr: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /><path d="M14 14h3v3h-3zM20 14v7h-3" /></svg>,
  truck: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 7h11v9H3zM14 10h4l3 3v3h-7z" /><circle cx="7" cy="18" r="1.6" /><circle cx="17" cy="18" r="1.6" /></svg>,
  info: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></svg>,
  ws: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 21V9l6 3V9l6 3V9l6 3v9z" /></svg>,
  cal: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>,
  note: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v12l-4 4H4z" /><path d="M16 20v-4h4M8 9h8M8 13h5" /></svg>,
  tag: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 12 12 20l-9-9V3h8z" /><circle cx="7.5" cy="7.5" r="1.2" /></svg>,
  img: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="9" cy="10" r="1.8" /><path d="m21 17-5-5-7 7" /></svg>,
  invoice: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M6 3h9l4 4v14H6z" /><path d="M15 3v4h4M9 12h6M9 16h6" /></svg>,
  cost: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="M14.5 9.5a2.5 2.5 0 0 0-5 0c0 1.5 1.2 2 2.5 2.5s2.5 1 2.5 2.5a2.5 2.5 0 0 1-5 0M12 6v1.5M12 16.5V18" /></svg>,
  clock: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>,
};

/**
 * Sipariş detayı — panel içi (masaüstü) görünüm. Başlık + hızlı işlemler, bilgi kartları,
 * para birimine göre maliyet özeti, atölye/model maliyet tabloları, sevkiyat ve gruplu geçmiş.
 */
const OrderDetail: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { has, hasModule } = usePermissions();

  const [order, setOrder] = useState<Order | null>(null);
  const [logs, setLogs] = useState<OrderLog[]>([]);
  const [wsCosts, setWsCosts] = useState<OrderWorkshopCost[]>([]);
  const [modelCosts, setModelCosts] = useState<ModelCost[]>([]);
  const [ship, setShip] = useState<OrderShipmentSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [alert, setAlert] = useState<{ isOpen: boolean; message: string; type: "info" | "success" | "warning" | "error" }>({ isOpen: false, message: "", type: "info" });

  const canCosts = user?.role === "Manager" || has("model-costs:view") || has("cost:items:view");
  const canPrice = user?.role === "Manager" || has("accounting-orders:view");

  const load = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    try {
      const [o, l] = await Promise.all([orderService.getById(orderId), orderService.getOrderLogs(orderId).catch(() => [])]);
      setOrder(o); setLogs(l as OrderLog[]);
    } catch { setOrder(null); setAlert({ isOpen: true, message: "Sipariş detayı yüklenemedi.", type: "error" }); }
    finally { setLoading(false); }
  }, [orderId]);
  useEffect(() => { load(); }, [load]);

  // Maliyet ve sevkiyat bağımsız; hata sayfayı düşürmez
  useEffect(() => {
    if (!order || !orderId) return;
    if (canCosts) {
      costService.getOrderWorkshopCosts(orderId).then(setWsCosts).catch(() => setWsCosts([]));
      if (order.modelId) costService.getOrderModelCosts(orderId, order.modelId).then(setModelCosts).catch(() => setModelCosts([]));
    }
    if (hasModule("shipping") && has("shipping:view")) shippingService.orderSummary(orderId).then(setShip).catch(() => setShip(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.orderId, order?.modelId, canCosts]);

  const groups = useMemo<LogGroup[]>(() => {
    const m = new Map<string, LogGroup>();
    for (const l of logs) {
      const k = l.changeToken || l.logId;
      if (!m.has(k)) m.set(k, { token: k, changedAt: l.changedAt, user: l.changedByUserName || "—", changes: [] });
      m.get(k)!.changes.push(l);
    }
    return Array.from(m.values()).sort((a, b) => new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime());
  }, [logs]);

  const wsTotals = useMemo(() => sumByCurrency(wsCosts), [wsCosts]);
  const mcTotals = useMemo(() => sumByCurrency(modelCosts), [modelCosts]);
  const grand = useMemo(() => { const g = { ...wsTotals }; for (const [c, v] of Object.entries(mcTotals)) g[c] = (g[c] || 0) + v; return g; }, [wsTotals, mcTotals]);

  if (loading) return <PageLoader message="Sipariş detayı yükleniyor..." />;
  if (!order) return (
    <div className="rc-page od-page"><div className="rc-card"><div className="rc-empty"><strong>Sipariş bulunamadı</strong>Silinmiş ya da bağlantı hatalı olabilir.<div style={{ marginTop: "0.75rem" }}><button className="rc-btn" onClick={() => navigate("/orders")}>Siparişlere dön</button></div></div></div></div>
  );

  const firm = order.firm, model = order.model;
  const dl = deadlineInfo(order.deadline, order.status);
  const done = order.status === OrderStatus.Tamamlandi;
  const images = [
    ...(model?.imageUrl ? [{ key: "model", url: model.imageUrl, label: "Model görseli", isModel: true }] : []),
    ...(order.images ?? []).map((im) => ({ key: im.orderImageId, url: getImageUrl(im.imageUrl), label: im.description || "Sipariş görseli", isModel: false })),
  ];
  const hasCosts = wsCosts.length > 0 || modelCosts.length > 0;

  return (
    <div className="rc-page od-page">
      <Link to="/orders" className="rc-crumb">{I.back}Siparişler</Link>

      {/* ===== Başlık ===== */}
      <div className="rc-card od-head">
        <div style={{ minWidth: 0 }}>
          <div className="od-kicker">Sipariş · kabul {fmtDate(order.acceptanceDate)}</div>
          <h1 className="od-title">
            {model?.modelCode || "Model kodu yok"}
            <span className={`od-status s${order.status ?? 1}`}>{STATUS_LABEL[order.status ?? 1] || "Bilinmiyor"}</span>
            {order.isSample && <span className="od-pill">Numune</span>}
            {order.priority && <span className="od-pill">Öncelik: {order.priority}</span>}
          </h1>
          <p className="od-sub"><b>{firm?.firmName || "Firma belirtilmemiş"}</b>{firm?.firmCode ? ` · ${firm.firmCode}` : ""}{model?.modelName ? ` · ${model.modelName}` : ""}</p>
        </div>
        <div className="od-actions">
          {has("orders:update") && <button className="rc-btn primary" onClick={() => setEditOpen(true)}>{I.edit}Düzenle</button>}
          <button className="rc-btn" onClick={() => navigate(`/orderdetail/${order.orderId}`)} title="Atölye transfer ekranı (QR)">{I.qr}Transfer Ekranı</button>
          {hasModule("shipping") && has("shipping:create") && !done && (order.firmId) && (
            <button className="rc-btn" onClick={() => navigate(`/shipping/new?firmId=${order.firmId}&orderId=${order.orderId}`)}>{I.truck}Sevkiyat Oluştur</button>
          )}
        </div>
      </div>

      {/* ===== KPI ===== */}
      <div className="rc-kpis" style={{ marginTop: "1rem" }}>
        <div className="rc-kpi"><div className="rc-kpi-label">Miktar</div><div className="rc-kpi-value">{fmtQty(order.quantity)} {UNIT_LABEL[order.unit ?? 1] || "Adet"}</div><div className="rc-kpi-sub">{order.pieceCount ? `${order.pieceCount} parça / takım` : order.width || order.height ? `${order.width ?? "—"} × ${order.height ?? "—"}` : " "}</div></div>
        <div className={`rc-kpi ${dl.tone === "overdue" ? "overdue" : dl.tone === "ok" ? "collected" : ""}`}><div className="rc-kpi-label">Termin</div><div className="rc-kpi-value">{order.deadline ? new Date(order.deadline).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" }) : "—"}</div><div className="rc-kpi-sub" style={dl.tone === "warn" ? { color: "var(--od-warn)" } : undefined}>{dl.text || "termin girilmemiş"}</div></div>
        <div className={`rc-kpi ${done ? "collected" : ""}`}><div className="rc-kpi-label">Atölye</div><div className="rc-kpi-value" style={{ fontSize: "1.1rem" }}>{order.workshop?.name || "Atanmadı"}</div><div className="rc-kpi-sub">{order.operator ? `${order.operator.firstName} ${order.operator.lastName}` : "sorumlu atanmamış"}</div></div>
        <div className="rc-kpi"><div className="rc-kpi-label">{done ? "Firmada geçen süre" : "Geçen süre"}</div><div className="rc-kpi-value">{spanText(order.createdAt, done ? order.completionDate : null) || "—"}</div><div className="rc-kpi-sub">{done ? `tamamlanma ${fmtDate(order.completionDate)}` : `açılış ${fmtDate(order.createdAt)}`}</div></div>
        {canPrice && order.price ? (
          <div className="rc-kpi"><div className="rc-kpi-label">Tutar</div><div className="rc-kpi-value">{formatCurrency(order.price * (order.quantity || 0), order.priceCurrency || "TRY")}</div><div className="rc-kpi-sub">birim {formatCurrency(order.price, order.priceCurrency || "TRY")}{order.vatRate != null ? ` · KDV %${order.vatRate}` : ""}</div></div>
        ) : null}
      </div>

      {/* ===== Bilgi kartları ===== */}
      <div className="od-grid">
        <section className="od-card">
          <div className="od-card-head"><h3>{I.info}Genel</h3></div>
          <div className="od-card-body"><dl className="od-dl">
            <div><dt>Firma</dt><dd>{firm?.firmName || "—"}</dd></div>
            {firm?.firmCode && <div><dt>Firma kodu</dt><dd>{firm.firmCode}</dd></div>}
            <div><dt>Model</dt><dd>{model?.modelCode || "—"}{model?.modelName ? ` · ${model.modelName}` : ""}</dd></div>
            {model?.category && <div><dt>Kategori</dt><dd>{model.category}</dd></div>}
            {model?.season && <div><dt>Sezon</dt><dd>{model.season}</dd></div>}
            {model?.color && <div><dt>Renk</dt><dd>{model.color}</dd></div>}
            {model?.size && <div><dt>Beden</dt><dd>{model.size}</dd></div>}
            {(order.invoice || order.invoiceNumber) && <div><dt>Fatura</dt><dd>{[order.invoice, order.invoiceNumber].filter(Boolean).join(" · ")}</dd></div>}
          </dl></div>
        </section>

        <section className="od-card">
          <div className="od-card-head"><h3>{I.ws}Atölye ve Sorumlular</h3></div>
          <div className="od-card-body"><dl className="od-dl">
            <div><dt>Atölye</dt><dd className={order.workshop ? "" : "muted"}>{order.workshop?.name || "Atanmadı"}</dd></div>
            <div><dt>Sorumlu</dt><dd className={order.operator ? "" : "muted"}>{order.operator ? `${order.operator.firstName} ${order.operator.lastName}` : "Atanmadı"}</dd></div>
            <div><dt>Modelist</dt><dd className={order.modelistUser ? "" : "muted"}>{order.modelistUser ? `${order.modelistUser.firstName} ${order.modelistUser.lastName}` : "Atanmadı"}</dd></div>
            <div><dt>Öncelik</dt><dd className={order.priority ? "" : "muted"}>{order.priority || "Normal"}</dd></div>
            {firm?.contactPerson && <div><dt>Firma yetkilisi</dt><dd>{firm.contactPerson}</dd></div>}
            {firm?.phone && <div><dt>Telefon</dt><dd><a href={`tel:${firm.phone.replace(/\s/g, "")}`}>{firm.phone}</a></dd></div>}
          </dl></div>
        </section>

        <section className="od-card">
          <div className="od-card-head"><h3>{I.cal}Tarihler</h3></div>
          <div className="od-card-body"><dl className="od-dl">
            <div><dt>Kabul</dt><dd>{fmtDate(order.acceptanceDate)}</dd></div>
            <div><dt>Termin</dt><dd style={dl.tone === "overdue" ? { color: "var(--od-danger)" } : undefined}>{fmtDate(order.deadline)}{dl.text ? ` · ${dl.text}` : ""}</dd></div>
            <div><dt>Tamamlanma</dt><dd className={order.completionDate ? "" : "muted"}>{order.completionDate ? fmtDateTime(order.completionDate) : "—"}</dd></div>
            <div><dt>Oluşturma</dt><dd className="muted">{fmtDateTime(order.createdAt)}{order.createdBy ? ` · ${order.createdBy}` : ""}</dd></div>
            {order.updatedAt && <div><dt>Son güncelleme</dt><dd className="muted">{fmtDateTime(order.updatedAt)}{order.updatedBy ? ` · ${order.updatedBy}` : ""}</dd></div>}
          </dl></div>
        </section>

        <section className={`od-card ${images.length > 0 ? "" : "span2"}`}>
          <div className="od-card-head"><h3>{I.note}Not</h3></div>
          <div className="od-card-body"><p className={`od-note ${order.note ? "" : "empty"}`}>{order.note || "Not eklenmemiş."}</p></div>
        </section>

        {order.orderTechnics && order.orderTechnics.length > 0 && (
          <section className="od-card">
            <div className="od-card-head"><h3>{I.tag}Teknikler</h3><span className="od-pill">{order.orderTechnics.length}</span></div>
            <div className="od-card-body"><div className="od-tags">{order.orderTechnics.map((ot, i) => <span key={ot.orderTechnicId || i} className="od-tag">{ot.technic?.name || "—"}</span>)}</div></div>
          </section>
        )}

        {images.length > 0 && (
          <section className={`od-card ${order.orderTechnics && order.orderTechnics.length > 0 ? "" : "span2"}`}>
            <div className="od-card-head"><h3>{I.img}Görseller</h3><span className="od-pill">{images.length}</span></div>
            <div className="od-card-body"><div className="od-images">
              {images.map((im) => (
                <div key={im.key} className={`od-img ${im.isModel ? "model" : ""}`} onClick={() => setLightbox(im.url)} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && setLightbox(im.url)}>
                  <img src={im.url} alt={im.label} loading="lazy" /><span>{im.label}</span>
                </div>
              ))}
            </div></div>
          </section>
        )}

        {ship && (
          <section className="od-card span3">
            <div className="od-card-head"><h3>{I.truck}Sevkiyat</h3><span className={`od-status ${ship.remaining <= 0.0005 ? "s4" : "s2"}`}>{fmtQty(ship.shipped)} / {fmtQty(ship.quantity)} sevk edildi</span></div>
            <div className="od-card-body">
              <div className={`od-progress ${ship.remaining <= 0.0005 ? "done" : ""}`}><span style={{ width: `${ship.quantity > 0 ? Math.min(100, (ship.shipped / ship.quantity) * 100) : 0}%` }} /></div>
              {ship.shipments.length === 0 ? <p className="od-note empty">Henüz sevkiyat yok.</p> : (
                <div className="rc-table-wrap"><table className="rc-table">
                  <thead><tr><th>Sevkiyat</th><th>Tarih</th><th>Durum</th><th>Araç</th><th className="num">Miktar</th></tr></thead>
                  <tbody>{ship.shipments.map((s) => (
                    <tr key={s.shipmentId} className="clickable" onClick={() => navigate(`/shipping/${s.shipmentId}`)}>
                      <td className="strong">{s.number}</td><td>{fmtDate(s.shippedAt || s.shipmentDate)}</td>
                      <td><span className={`od-status s${s.status === ShipmentStatus.Delivered ? 4 : s.status === ShipmentStatus.Cancelled ? 3 : s.status === ShipmentStatus.Shipped ? 2 : 1}`}>{s.status === ShipmentStatus.Delivered ? "Teslim edildi" : s.status === ShipmentStatus.Cancelled ? "İptal" : s.status === ShipmentStatus.Shipped ? "Yolda" : "Taslak"}</span></td>
                      <td>{[s.carrier, s.vehiclePlate].filter(Boolean).join(" · ") || "—"}</td><td className="num">{fmtQty(s.totalQuantity)}</td>
                    </tr>
                  ))}</tbody>
                </table></div>
              )}
            </div>
          </section>
        )}
      </div>

      {/* ===== Maliyet ===== */}
      {canCosts && hasCosts && (
        <>
          <div className="od-costband">
            <div><h2>Maliyet özeti</h2><p>Atölye ve model maliyetleri, para birimine göre ayrı toplanır.</p></div>
            <div className="od-cost"><div className="lbl">Atölye</div><div className="val">{moneyList(wsTotals).map((t) => <small key={t}>{t}</small>)}{wsCosts.length === 0 && "—"}</div><div className="sub">{wsCosts.length} kalem</div></div>
            <div className="od-cost"><div className="lbl">Model</div><div className="val">{moneyList(mcTotals).map((t) => <small key={t}>{t}</small>)}{modelCosts.length === 0 && "—"}</div><div className="sub">{modelCosts.length} kalem</div></div>
            <div className="od-cost grand"><div className="lbl">Genel toplam</div><div className="val">{moneyList(grand).map((t) => <small key={t}>{t}</small>)}</div><div className="sub">{order.quantity ? `birim başına ${moneyList(Object.fromEntries(Object.entries(grand).map(([c, v]) => [c, v / order.quantity]))).join(" · ")}` : ""}</div></div>
          </div>

          {wsCosts.length > 0 && (
            <div className="rc-card">
              <div className="rc-card-head"><div><h2>Atölye maliyetleri</h2><p>Transfer sırasında girilen sarf ve işçilik kalemleri</p></div><span className="od-table-hint">{new Set(wsCosts.map((c) => c.workshopId)).size} atölye · {wsCosts.length} kalem</span></div>
              <div className="rc-table-wrap"><table className="rc-table">
                <thead><tr><th>Atölye</th><th>Kalem</th><th>Kategori</th><th className="num">Miktar</th><th className="num">Birim fiyat</th><th className="num">Toplam</th><th>Not</th></tr></thead>
                <tbody>{wsCosts.map((c) => (
                  <tr key={c.orderWorkshopCostId}>
                    <td className="strong">{c.workshop?.name || "—"}</td><td>{c.costItem?.itemName || "—"}</td>
                    <td><span className="od-cat">{c.costItem?.costCategory?.categoryName || "—"}</span></td>
                    <td className="num">{fmtQty(c.quantityUsed)}<span className="od-unit">{c.costItem?.unit || ""}</span></td>
                    <td className="num">{formatCurrency(c.actualPrice, c.currency)}</td><td className="num strong">{formatCurrency(c.totalCost, c.currency)}</td>
                    <td className="muted">{c.notes || "—"}</td>
                  </tr>
                ))}</tbody>
                <tfoot><tr><td colSpan={5} style={{ textAlign: "right" }}>Toplam</td><td className="num strong">{moneyList(wsTotals).join(" · ")}</td><td /></tr></tfoot>
              </table></div>
            </div>
          )}

          {modelCosts.length > 0 && (
            <div className="rc-card">
              <div className="rc-card-head"><div><h2>Model maliyetleri</h2><p>Reçete ve fire dahil hesaplanan kalemler</p></div><span className="od-table-hint">{modelCosts.length} kalem</span></div>
              <div className="rc-table-wrap"><table className="rc-table">
                <thead><tr><th>#</th><th>Kalem</th><th>Kategori</th><th>Atölye</th><th className="num">Miktar</th><th className="num">2. miktar</th><th className="num">Fire</th><th className="num">Net</th><th className="num">Birim fiyat</th><th className="num">Toplam</th><th>Kullanım</th></tr></thead>
                <tbody>{[...modelCosts].sort((a, b) => (a.priority || 999) - (b.priority || 999)).map((c) => (
                  <tr key={c.modelCostId}>
                    <td><span className="od-prio">{c.priority ?? "—"}</span></td>
                    <td className="strong">{c.costItem?.itemName || "—"}</td>
                    <td><span className="od-cat">{c.costItem?.costCategory?.categoryName || "—"}</span></td>
                    <td>{c.oldWorkshopName || "—"}</td>
                    <td className="num">{fmtQty(c.quantity)}<span className="od-unit">{c.costItem?.unit || ""}</span></td>
                    <td className="num">{c.quantity2 ? <>{fmtQty(c.quantity2)}<span className="od-unit">{c.unit2 || ""}</span><span className="od-sub-num">{fmtQty(c.quantity)} × {fmtQty(c.quantity2)} = {formatNumber(c.quantity * c.quantity2)}</span></> : <span className="muted">—</span>}</td>
                    <td className="num">{c.costItem?.wastagePercentage ? `%${c.costItem.wastagePercentage}` : "—"}</td>
                    <td className="num strong">{c.actualQuantityNeeded ? formatNumber(c.actualQuantityNeeded) : fmtQty(c.quantity)}</td>
                    <td className="num">{c.unitPrice ? formatCurrency(c.unitPrice, c.currency || "TRY") : "—"}</td>
                    <td className="num strong">{c.totalCost ? formatCurrency(c.totalCost, c.currency || "TRY") : "—"}</td>
                    <td className="muted">{c.usage || "—"}</td>
                  </tr>
                ))}</tbody>
                <tfoot><tr><td colSpan={9} style={{ textAlign: "right" }}>Toplam</td><td className="num strong">{moneyList(mcTotals).join(" · ")}</td><td /></tr></tfoot>
              </table></div>
            </div>
          )}
        </>
      )}

      {/* ===== Geçmiş ===== */}
      <div className="rc-card">
        <div className="rc-card-head"><div><h2>Sipariş geçmişi</h2><p>Aynı işlemde yapılan değişiklikler birlikte gösterilir</p></div><span className="od-table-hint">{groups.length} işlem</span></div>
        {groups.length === 0 ? <div className="rc-empty"><strong>Henüz geçmiş kaydı yok</strong></div> : (
          <ul className="od-history">
            {groups.map((g) => (
              <li key={g.token} className="od-h">
                <div className="od-h-head"><span className="od-h-user">{g.user}</span><span className="od-h-when">{fmtDateTime(g.changedAt)}</span>{g.changes.length > 1 && <span className="od-h-count">{g.changes.length} değişiklik</span>}</div>
                <ul className="od-h-list">
                  {g.changes.map((l) => (
                    <li key={l.logId} className="od-h-row">
                      <span className="od-h-field">{fieldLabel(l)}</span>
                      <span>
                        {l.oldValue || l.newValue ? (<>{l.oldValue && <span className="od-h-old">{l.oldValue}</span>}{l.oldValue && l.newValue && <span className="od-h-arrow">→</span>}{l.newValue && <span className="od-h-new">{l.newValue}</span>}</>) : <span className="od-h-desc">{l.changeDescription || "—"}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </div>

      {lightbox && (
        <div className="od-lightbox" onClick={() => setLightbox(null)}>
          <button aria-label="Kapat" onClick={() => setLightbox(null)}>✕</button>
          <img src={lightbox} alt="Görsel" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {/* Düzenleme sayfaya gitmez; kayıttan sonra detay ve geçmiş yeniden yüklenir. */}
      <OrderEditModal
        isOpen={editOpen}
        orderId={order.orderId}
        onClose={() => setEditOpen(false)}
        onSave={() => { setEditOpen(false); load(); }}
      />

      <AlertModal isOpen={alert.isOpen} message={alert.message} type={alert.type} onClose={() => setAlert({ ...alert, isOpen: false })} />
    </div>
  );
};

export default OrderDetail;
